import { useEffect, useState, useCallback } from 'react'
import { useLocation, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Auto-derives the help slug from the current route.  Add a new mapping here
// when a new top-level page ships.  Order matters — first prefix match wins,
// so put more-specific paths (matter-detail) before their parents (matters).
const ROUTE_TO_SLUG = [
  { prefix: '/matters/', slug: 'matter-detail' },
  { prefix: '/matters',  slug: 'matters' },
  { prefix: '/clients/merge', slug: 'client-merge' },
  { prefix: '/clients',  slug: 'clients' },
  { prefix: '/contacts', slug: 'contacts' },
  { prefix: '/dailies',  slug: 'dailies' },
  { prefix: '/dashboard',slug: 'dashboard' },
  { prefix: '/time-entries',       slug: 'time-entries' },
  { prefix: '/time-entry-duplicates', slug: 'troubleshooting' },
  { prefix: '/calendar', slug: 'calendar' },
  { prefix: '/deadlines',slug: 'deadlines' },
  { prefix: '/tasks',    slug: 'tasks' },
  { prefix: '/billing',  slug: 'billing' },
  { prefix: '/batch-invoicing', slug: 'batch-invoicing' },
  { prefix: '/invoices', slug: 'invoices' },
  { prefix: '/receipts', slug: 'receipts' },
  { prefix: '/trust',    slug: 'trust' },
  { prefix: '/communications', slug: 'communications' },
  { prefix: '/sol',      slug: 'sol' },
  { prefix: '/conflicts',slug: 'conflicts' },
  { prefix: '/court-rules', slug: 'court-rules' },
  { prefix: '/discovery',slug: 'discovery' },
  { prefix: '/reports',  slug: 'reports' },
  { prefix: '/settings', slug: 'firm-settings' },
  { prefix: '/team',     slug: 'team' },
  { prefix: '/profile',  slug: 'profile' },
  { prefix: '/admin',    slug: 'admin' },
  { prefix: '/audit-log',slug: 'audit-log' },
  { prefix: '/feedback', slug: 'feedback-inbox' },
  { prefix: '/import',   slug: 'import' },
  { prefix: '/downloads',slug: 'desktop-install' },
  { prefix: '/search',   slug: 'search' },
]

export function slugForPath(pathname) {
  // Exact matches for /matters vs /matters/:id — the trailing slash of /matters/
  // in the map wouldn't match plain /matters.  Handle both.
  if (pathname === '/matters') return 'matters'
  if (pathname === '/clients') return 'clients'
  for (const entry of ROUTE_TO_SLUG) {
    if (pathname === entry.prefix.replace(/\/$/, '') || pathname.startsWith(entry.prefix)) {
      return entry.slug
    }
  }
  return null
}

// Extract the first h1 from raw markdown so we can title the panel.
function firstHeading(md) {
  const m = md.match(/^#\s+(.+?)\s*$/m)
  return m ? m[1] : 'Help'
}

// Floating ? button in the top-right corner + slide-in side panel.
// The panel loads the markdown for the current page's help slug.
export default function ContextualHelp() {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const slug = slugForPath(location.pathname)

  const loadContent = useCallback(async () => {
    if (!slug) return
    setLoading(true); setError(''); setContent('')
    try {
      const res = await fetch(`/help/${slug}.md`, { cache: 'no-cache' })
      if (!res.ok) throw new Error(`Help doc not found (${res.status})`)
      const text = await res.text()
      setContent(text)
    } catch (e) {
      setError(e.message || 'Could not load help')
    } finally {
      setLoading(false)
    }
  }, [slug])

  // Fetch when the panel opens (not on route change while closed — keeps
  // network idle for users who never open the panel).
  useEffect(() => {
    if (open && slug) loadContent()
  }, [open, slug, loadContent])

  // Route change closes the panel automatically — the help was for the OLD
  // page.  Reopen if the user wants context on the new page.
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Keyboard shortcut: `?` (Shift+/) opens the panel from anywhere except
  // when typing into an input / textarea / contenteditable.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '?' && !(e.key === '/' && e.shiftKey)) return
      const t = e.target
      const tag = (t && t.tagName) || ''
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (t && t.isContentEditable)
      if (isEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (!slug) return
      e.preventDefault()
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [slug])

  // Also listen for a window event fired by the sidebar's ? button.  Keeps
  // the trigger + panel decoupled — the button lives in Layout.js next to
  // the refresh button so it doesn't collide with page action buttons in
  // the top-right.
  useEffect(() => {
    const openHandler = () => { if (slug) setOpen(true) }
    window.addEventListener('open-contextual-help', openHandler)
    return () => window.removeEventListener('open-contextual-help', openHandler)
  }, [slug])

  // Esc closes the panel — and claims that Esc (preventDefault), so a page
  // listening for Esc as well (Calendar's "Esc leaves full screen") leaves it
  // alone: this Esc only closes the help.
  useEffect(() => {
    if (!open) return
    const onEsc = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false) } }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [open])

  // Hide the button on the /help page itself and any /popup/* route (those
  // windows have their own tight UI and the button would occlude).
  if (!slug) return null
  if (location.pathname === '/help' || location.pathname.startsWith('/help#')) return null
  if (location.pathname.startsWith('/popup/')) return null

  const title = content ? firstHeading(content) : 'Help'

  // Trigger button now lives in Layout.js sidebar header (next to the
  // refresh button) so it doesn't collide with page action buttons in
  // the top-right.  This component renders only the slide-in panel +
  // owns the open/close state; the button dispatches an
  // 'open-contextual-help' window event that we listen for above.
  return (
    <>
      {open && (
        <>
          {/* Backdrop — click closes.  Semi-transparent so the app stays visible. */}
          <div
            className="fixed inset-0 z-[70] bg-black/30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/* Side panel — slides in from the right.  440 px at zoom 1, times
              the automatic zoom (--app-zoom, lib/displayZoom.js): its text is
              rem, so a fixed 440 px held four words a line at a 4K screen's
              2.00.  Never wider than the window. */}
          <div
            role="dialog"
            aria-label="Contextual help"
            className="fixed top-0 right-0 bottom-0 z-[71] w-full sm:w-[calc(440px*var(--app-zoom,1))] sm:max-w-full bg-white shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">📖 Help</p>
                <h2 className="text-base font-semibold text-gray-800 truncate">{title}</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={`/help#${slug}`}
                  onClick={() => setOpen(false)}
                  className="text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap"
                  title="Open in the full Help page">
                  Full docs ↗
                </Link>
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
                  aria-label="Close help panel">
                  ✕
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading && <p className="text-sm text-gray-400">Loading…</p>}
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                  {error}
                </div>
              )}
              {!loading && !error && content && (
                <article className="prose prose-sm max-w-none prose-headings:scroll-mt-4 prose-a:text-primary-600">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content}
                  </ReactMarkdown>
                </article>
              )}
            </div>
            <div className="border-t border-gray-100 px-4 py-2 text-[10px] text-gray-400 text-center">
              Press <kbd className="bg-gray-100 border border-gray-300 rounded px-1 py-0.5 font-sans">?</kbd> anywhere · <kbd className="bg-gray-100 border border-gray-300 rounded px-1 py-0.5 font-sans">Esc</kbd> to close
            </div>
          </div>
        </>
      )}
    </>
  )
}
